import {
	createHoverActionsContainer,
	NAVIGATION_ACTIONS,
} from "../components/header-components/ActionButtons";
import { HeaderElement } from "../types";

// NavItem type definition
interface NavItem {
	id: string;
	text: string;
	url?: string;
	isActive?: boolean;
}

// NavItems Component as HeaderElement Object
const navItemsElement: HeaderElement = {
	id: "navitems-1",
	type: "NavItems",
	tagName: "div",
	data: {
		// Component settings/configuration
		items: [
			{ id: "nav-1", text: "Home", url: "/", isActive: false },
			{ id: "nav-2", text: "About", url: "/about", isActive: false },
			{ id: "nav-3", text: "Services", url: "/services", isActive: true },
			{ id: "nav-4", text: "Contact", url: "/contact", isActive: false },
		],

		// Typography settings
		fontWeight: "normal", // "normal", "bold", "600", etc.
		textDecoration: "none", // "none", "underline", etc.
		fontStyle: "normal", // "normal", "italic"

		// Layout settings
		gap: "16px",

		// Component behavior settings
		isSelectable: true,
		isDeletable: true,
		hasHoverActions: true,

		// Event handlers
		onClick: "handleNavItemsClick",
		onRemove: "handleNavItemsRemove",
		onEdit: "handleNavItemsEdit",
		onNavItemClick: "handleNavItemClick",
	},
	className: "navitems-container",
	style: {
		position: "relative",
		cursor: "pointer",
		padding: "8px",
	},
	props: {
		onClick: "handleClick",
		role: "navigation",
		tabIndex: 0,
	},
	children: [
		// Nav Items Container
		{
			id: "navitems-list-container",
			type: "NavItemsContainer",
			tagName: "div",
			data: {
				gap: "16px",
			},
			className: "nav-items-list",
			style: {
				display: "flex",
				alignItems: "center",
			},
			children: [
				// Individual Nav Items (these would be generated dynamically)
				{
					id: "navitem-home",
					type: "NavItem",
					tagName: "div",
					data: {
						text: "Home",
						url: "/",
						isActive: false,
					},
					className: "nav-item",
					props: {
						onClick: "handleNavItemClick",
					},
					children: [],
				},
				{
					id: "navitem-about",
					type: "NavItem",
					tagName: "div",
					data: {
						text: "About",
						url: "/about",
						isActive: false,
					},
					className: "nav-item",
					props: {
						onClick: "handleNavItemClick",
					},
					children: [],
				},
				{
					id: "navitem-services",
					type: "NavItem",
					tagName: "div",
					data: {
						text: "Services",
						url: "/services",
						isActive: true,
					},
					className: "nav-item active",
					props: {
						onClick: "handleNavItemClick",
					},
					children: [],
				},
				{
					id: "navitem-contact",
					type: "NavItem",
					tagName: "div",
					data: {
						text: "Contact",
						url: "/contact",
						isActive: false,
					},
					className: "nav-item",
					props: {
						onClick: "handleNavItemClick",
					},
					children: [],
				},
			],
		},
	],

	css: `
        .navitems-container {
            position: relative;
            cursor: pointer;
            padding: 8px;
        }
        
        .navitems-container.selected {
            border: 2px dashed #2196f3;
        }
        
        /* Nav Items List Container */
        .nav-items-list {
            display: flex;
            align-items: center;
            gap: 16px;
        }
        
        /* Individual Nav Item */
        .nav-item {
            cursor: pointer;
            font-weight: normal;
            text-decoration: none;
            font-style: normal;
            transition: color 0.2s ease;
            padding: 4px 8px;
            border-radius: 4px;
        }
        
        .nav-item:hover {
            color: #3b82f6;
        }
        
        .nav-item.active {
            color: #3b82f6;
            font-weight: 600;
        }
        
        /* Typography Variations */
        .nav-item.font-weight-bold {
            font-weight: bold;
        }
        
        .nav-item.font-weight-600 {
            font-weight: 600;
        }
        
        .nav-item.text-decoration-underline {
            text-decoration: underline;
        }
        
        .nav-item.font-style-italic {
            font-style: italic;
        }
        
        /* Hover Actions */
        .hover-actions {
            position: absolute;
            top: 0;
            right: 0;
            opacity: 0;
            background-color: rgba(0, 0, 0, 0.7);
            padding: 4px;
            border-radius: 4px;
            display: flex;
            transition: opacity 0.2s ease;
        }
        
        .navitems-container:hover .hover-actions {
            opacity: 1;
        }
        
        .action-btn {
            padding: 4px 8px;
            margin: 0 2px;
            color: white;
            background: none;
            border: none;
            cursor: pointer;
            border-radius: 2px;
            transition: all 0.2s ease;
        }
        
        .edit-btn:hover {
            color: #64b5f6;
            transform: scale(1.1);
        }
        
        .remove-btn:hover {
            color: #f48fb1;
            transform: scale(1.1);
        }
        
        .btn-icon {
            font-size: 12px;
            display: inline-block;
        }
    `,
};

// Factory function to create nav items elements with different configurations
const createNavItemsElement = (
	config: {
		id: string;
		items: NavItem[];
		fontWeight?: string;
		textDecoration?: string;
		fontStyle?: string;
		gap?: string;
		customStyles?: React.CSSProperties;
		menuSlug?: string; // Optional slug for menu items
	},
	publishMode = false
): HeaderElement => {
	// Generate nav item children based on the items array
	const navItemChildren: HeaderElement[] = config.items.map((item, index) => {
		const navItemClasses = ["nav-item"];

		// Add active class if item is active
		if (item.isActive) {
			navItemClasses.push("active");
		}

		// Add typography classes
		if (config.fontWeight && config.fontWeight !== "normal") {
			navItemClasses.push(`font-weight-${config.fontWeight}`);
		}

		if (config.textDecoration && config.textDecoration !== "none") {
			navItemClasses.push(`text-decoration-${config.textDecoration}`);
		}

		if (config.fontStyle && config.fontStyle !== "normal") {
			navItemClasses.push(`font-style-${config.fontStyle}`);
		}

		return {
			id: `${config.id}-navitem-${index}`,
			type: "NavItem",
			tagName: "div",
			data: {
				text: item.text,
				url: item.url,
				isActive: item.isActive,
				index: index,
			},
			className: navItemClasses.join(" "),
			props: {
				onClick: "handleNavItemClick",
			},
			children: [],
		};
	});

	const element: HeaderElement = {
		id: config.id,
		type: "NavItems",
		tagName: "div",
		data: {
			items: config.items,
			fontWeight: config.fontWeight || "normal",
			textDecoration: config.textDecoration || "none",
			fontStyle: config.fontStyle || "normal",
			gap: config.gap || "16px",
			isSelectable: true,
			isDeletable: true,
			hasHoverActions: true,
			menuSlug: config.menuSlug || "", // Optional slug for menu items
		},
		className: "navitems-container",
		props: { onClick: "handleEditClick" },
		style: {
			position: "relative",
			cursor: "pointer",
			padding: "8px",
			...config.customStyles,
		},
		children: [
			// Nav Items Container
			{
				id: `${config.id}-list-container`,
				type: "NavItemsContainer",
				tagName: "div",
				className: "nav-items-list",
				data: {
					gap: config.gap || "16px",
				},
				style: {
					display: "flex",
					alignItems: "center",
					gap: config.gap || "16px",
				},
				children: navItemChildren,
			},

			// Hover Actions Container
			...[
				!publishMode
					? createHoverActionsContainer(config.id, NAVIGATION_ACTIONS)
					: ([] as any),
			],
		],
		css: `
            .navitems-container {
                position: relative;
                cursor: pointer;
                padding: 8px;
            }
            
            .navitems-container.selected {
                border: 2px dashed #2196f3;
            }
            
            .nav-items-list {
                display: flex;
                align-items: center;
            }
            
            .nav-item {
                cursor: pointer;
                font-weight: ${config.fontWeight || "normal"};
                text-decoration: ${config.textDecoration || "none"};
                font-style: ${config.fontStyle || "normal"};
                transition: color 0.2s ease;
                padding: 4px 8px;
                border-radius: 4px;
            }
            
            .nav-item:hover {
                color: #3b82f6;
            }
            
            .nav-item.active {
                color: #3b82f6;
                font-weight: 600;
            }
            
            .font-weight-bold {
                font-weight: bold !important;
            }
            
            .font-weight-600 {
                font-weight: 600 !important;
            }
            
            .text-decoration-underline {
                text-decoration: underline !important;
            }
            
            .font-style-italic {
                font-style: italic !important;
            }
        `,
	};

	return element;
};

// Simplified version for basic navigation
const createSimpleNavItemsElement = (config: {
	id: string;
	items: string[]; // Just text items
	gap?: string;
}): HeaderElement => {
	const navItems: NavItem[] = config.items.map((text, index) => ({
		id: `nav-${index}`,
		text: text,
		url: `/${text.toLowerCase()}`,
		isActive: index === 0, // First item active by default
	}));

	return createNavItemsElement({
		id: config.id,
		items: navItems,
		gap: config.gap,
	});
};

export { createNavItemsElement, createSimpleNavItemsElement, navItemsElement };
